import axios from 'axios';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use((config) => {
  let token = null;
  try { token = localStorage.getItem('rl_token'); } catch (e) { /* storage blocked */ }
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const Products = {
  list: (params = {}) => api.get('/products', { params }).then((r) => r.data),
  adminList: () => api.get('/admin/products').then((r) => r.data),
  get: (slug) => api.get(`/products/${slug}`).then((r) => r.data),
  create: (data) => api.post('/products', data).then((r) => r.data),
  update: (id, data) => api.put(`/products/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/products/${id}`).then((r) => r.data),
};

export const Categories = {
  list: () => api.get('/categories').then((r) => r.data),
  create: (data) => api.post('/categories', data).then((r) => r.data),
  update: (id, data) => api.put(`/categories/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/categories/${id}`).then((r) => r.data),
};

export const Banners = {
  list: () => api.get('/banners').then((r) => r.data),
  adminList: () => api.get('/admin/banners').then((r) => r.data),
  create: (data) => api.post('/banners', data).then((r) => r.data),
  update: (id, data) => api.put(`/banners/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/banners/${id}`).then((r) => r.data),
};

export const Coupons = {
  list: () => api.get('/coupons').then((r) => r.data),
  public: () => api.get('/coupons/public').then((r) => r.data),
  create: (data) => api.post('/coupons', data).then((r) => r.data),
  remove: (id) => api.delete(`/coupons/${id}`).then((r) => r.data),
  apply: (code, subtotal) => api.post('/coupons/apply', { code, subtotal }).then((r) => r.data),
};

export const Orders = {
  create: (data) => api.post('/orders', data).then((r) => r.data),
  my: () => api.get('/orders/my').then((r) => r.data),
  get: (id) => api.get(`/orders/${id}`).then((r) => r.data),
  adminList: (status) => api.get('/admin/orders', { params: status ? { status } : {} }).then((r) => r.data),
  adminUpdate: (id, data) => api.put(`/admin/orders/${id}`, data).then((r) => r.data),
  ship: (id) => api.post(`/admin/orders/${id}/ship`).then((r) => r.data),
  track: (id) => api.get(`/admin/orders/${id}/track`).then((r) => r.data),
};

export const Auth = {
  login: (data) => api.post('/auth/login', data).then((r) => r.data),
  register: (data) => api.post('/auth/register', data).then((r) => r.data),
  me: () => api.get('/auth/me').then((r) => r.data),
  googleSession: (session_id) => api.post('/auth/google/session', { session_id }).then((r) => r.data),
  firebasePhone: (id_token) => api.post('/auth/firebase-phone', { id_token }).then((r) => r.data),
  firebasePhoneLink: (id_token) => api.post('/auth/firebase-phone/link', { id_token }).then((r) => r.data),
  updateMe: (payload) => api.patch('/auth/me', payload).then((r) => r.data),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }).then((r) => r.data),
  resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }).then((r) => r.data),
};

export const Addresses = {
  list: () => api.get('/addresses').then((r) => r.data),
  create: (data) => api.post('/addresses', data).then((r) => r.data),
  update: (id, data) => api.put(`/addresses/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/addresses/${id}`).then((r) => r.data),
  setDefault: (id) => api.put(`/addresses/${id}`, { is_default: true }).then((r) => r.data),
};

export const Blog = {
  list: (params = {}) => api.get('/blog', { params }).then((r) => r.data),
  get: (slug) => api.get(`/blog/${slug}`).then((r) => r.data),
  create: (data) => api.post('/blog', data).then((r) => r.data),
  update: (id, data) => api.put(`/blog/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/blog/${id}`).then((r) => r.data),
  adminList: () => api.get('/admin/blog').then((r) => r.data),
};

export const Admin = {
  stats: () => api.get('/admin/stats').then((r) => r.data),
  users: () => api.get('/admin/users').then((r) => r.data),
  createUser: (data) => api.post('/admin/users', data).then((r) => r.data),
  updateUser: (id, data) => api.patch(`/admin/users/${id}`, data).then((r) => r.data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`).then((r) => r.data),
  passwordResets: () => api.get('/admin/password-resets').then((r) => r.data),
  importProducts: (file, dryRun) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/admin/products/import?dry_run=${dryRun ? 'true' : 'false'}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data);
  },
  uploadImage: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/admin/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data);
  },
  uploadVideo: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/admin/upload-video', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data);
  },
  getSettings: () => api.get('/settings').then((r) => r.data),
  updateSettings: (data) => api.put('/admin/settings', data).then((r) => r.data),
  bannersZipUrl: () => `${API_BASE}/admin/banners/download-zip`,
  productsZipUrl: () => `${API_BASE}/admin/products/download-images-zip`,
};

export const B2BCatalog = {
  adminList: () => api.get('/admin/b2b/products').then((r) => r.data),
  create: (data) => api.post('/admin/b2b/products', data).then((r) => r.data),
  update: (id, data) => api.put(`/admin/b2b/products/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/admin/b2b/products/${id}`).then((r) => r.data),
  import: (file, dryRun) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/admin/b2b/products/import?dry_run=${dryRun ? 'true' : 'false'}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data);
  },
};

// For the Download buttons we need to attach the auth token manually since
// the browser follows the href as a top-level GET (no Axios interceptor).
export const authedDownload = async (url, filename) => {
  let token = null;
  try { token = localStorage.getItem('rl_token'); } catch (e) { /* storage blocked */ }
  const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  const blob = await res.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
};
